"use client";

import HierarchicalLocationSelect from "@/components/ui/HierarchicalLocationSelect";
import FieldError from "@/components/ui/FieldError";
import type { HierarchicalLocationOnChange } from "@/lib/locationIds";
import {
  locationStringsMatch,
  resolveProfileLocationApplyArgs,
  type SellProfileLocation,
} from "@/lib/listing/listingLocationIds";

export interface SellLocationFieldProps {
  value: string;
  onChange: HierarchicalLocationOnChange;
  error?: string;
  /** @deprecated Prefer `profileLocation` — kept for call-site compatibility. */
  userLocation?: string;
  profileLocation?: SellProfileLocation | null;
  placeholder?: string;
  className?: string;
  label?: string;
}

export default function SellLocationField({
  value,
  onChange,
  error,
  userLocation,
  profileLocation,
  placeholder = "Select your location",
  className = "w-full",
  label = "Item Location",
}: SellLocationFieldProps) {
  const profile =
    profileLocation ??
    (userLocation?.trim()
      ? ({ location: userLocation.trim() } satisfies SellProfileLocation)
      : null);

  const profileLabel = profile?.location.trim() ?? "";
  const hasProfile = Boolean(profileLabel);
  const matchesProfile =
    hasProfile && locationStringsMatch(value, profileLabel);
  const hasDifferentSelection =
    hasProfile && Boolean(value.trim()) && !matchesProfile;
  const showEmptyShortcut = hasProfile && !value.trim();

  const applyProfileLocation = () => {
    if (!profile) return;
    const args = resolveProfileLocationApplyArgs(profile);
    onChange(args.value, args.coordinates, args.placeId, args.locationIds);
  };

  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-foreground">
        {label} <span className="text-red-500">*</span>
      </label>
      <HierarchicalLocationSelect
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
      />
      <FieldError message={error} />

      {showEmptyShortcut ? (
        <div className="mt-3 space-y-1.5">
          <button
            type="button"
            onClick={applyProfileLocation}
            className="w-full rounded-xl border border-primary-200 bg-primary-50/80 px-3 py-2.5 text-left transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <span className="block text-sm font-medium text-foreground">
              Use my profile location
            </span>
            <span className="mt-0.5 block text-xs text-text-light">
              {profileLabel}
            </span>
          </button>
          <p className="text-xs text-text-light">
            Or choose a different area in the picker above
          </p>
        </div>
      ) : null}

      {matchesProfile ? (
        <p className="mt-2 text-xs text-text-light">
          From your profile
          <span className="mx-1.5 text-border">·</span>
          <button
            type="button"
            onClick={() => onChange("")}
            className="font-medium text-primary-800 underline-offset-2 hover:underline"
          >
            Change
          </button>
        </p>
      ) : null}

      {hasDifferentSelection ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-light">
          <span>Prefer your usual area?</span>
          <button
            type="button"
            onClick={applyProfileLocation}
            className="font-medium text-primary-800 underline-offset-2 hover:underline"
          >
            Use {profileLabel} instead
          </button>
        </div>
      ) : null}
    </div>
  );
}
